import { useEffect, useMemo, useRef, useState } from 'react';

import { EntityRefUtil, RecordState } from '@gearbelt/global';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import AssetQrCodesIcon from '@/assets/qrcodes/asset-qr-codes.svg';
import BlankQrCodesIcon from '@/assets/qrcodes/blank-qr-codes.svg';
import QrCodeIcon from '@/components/icons/qr-code';
import QuestionIcon from '@/components/icons/question';
import { TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { getQRCodesQueryOptions } from '@/queries/assets';

import { QRPrintDocument } from './qr-print-document';

export function QRDialog({
  open,
  setOpen,
  setQRBlankDialogOpen,
  assetState,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  setQRBlankDialogOpen: () => void;
  assetState: RecordState;
}) {
  const [toPrintQRCodes, setToPrintQRCodes] = useState<{ label?: string; qr: string }[]>([]);
  const [isAssetQRCodesGenerating, setIsAssetQRCodesGenerating] = useState(false);

  const { data: qrCodes, isFetching: isFetchingQRCodes } = useQuery(getQRCodesQueryOptions({ assetState }));

  const assignedQRCodes = useMemo(() => qrCodes?.filter(({ asset }) => asset) || [], [qrCodes]);

  const contentRef = useRef<HTMLDivElement>(null);

  const handlePrintAssetQrCodes = useReactToPrint({
    documentTitle: 'Gearbelt Asset QR Codes',
    contentRef,
    onAfterPrint: () => {
      setIsAssetQRCodesGenerating(false);
      setToPrintQRCodes([]);
    },
  });

  useEffect(() => {
    if (!isFetchingQRCodes && assignedQRCodes && assignedQRCodes.length > 0 && toPrintQRCodes.length > 0) {
      handlePrintAssetQrCodes();
    }
  }, [isFetchingQRCodes, qrCodes, toPrintQRCodes]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <TablePageHeaderActionButton icon={<QrCodeIcon />} label="Generate QR Codes" />
      </DialogTrigger>
      <DialogContent className="flex h-fit w-[500px] flex-col gap-3">
        <DialogHeader>
          <DialogTitle>Generate QR Codes</DialogTitle>
        </DialogHeader>

        <div className="text-default-text text-sm">
          Print QR codes and stick them to your assets so that anyone can access the asset profile and submit forms related to that asset.
          <div className="mt-2">
            <Link
              to="https://help.sitemate.com/en/articles/10074940-how-to-assign-gearbelt-blank-qr-codes-to-assets"
              target="_blank"
              className="bg-grey-1 inline-flex w-auto gap-1 rounded p-1 text-sm">
              <QuestionIcon className="h-4 w-4" />
              Learn more
            </Link>
          </div>
        </div>

        <div className="flex gap-4">
          {!!assignedQRCodes.length && (
            <QRCodeTypeOverview
              iconSrc={AssetQrCodesIcon}
              heading="Asset QR Codes"
              description="Get a PDF containing the QR codes of all your assets."
              isGenerating={isAssetQRCodesGenerating}
              onClick={() => {
                setIsAssetQRCodesGenerating(true);
                setToPrintQRCodes(
                  assignedQRCodes.map((qrCode) => {
                    return {
                      label: EntityRefUtil.createAssetLabel(qrCode.asset),
                      qr: qrCode.value,
                    };
                  }),
                );
              }}
            />
          )}

          <QRCodeTypeOverview
            iconSrc={BlankQrCodesIcon}
            heading="Blank QR Codes"
            description="Get a PDF containing blank QR codes which can be assigned to assets later."
            isGenerating={false}
            onClick={() => {
              setQRBlankDialogOpen();
            }}
          />
        </div>

        <DialogFooter>
          <QRPrintDocument contentRef={contentRef} qrs={toPrintQRCodes} type="assets" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QRCodeTypeOverview({
  iconSrc,
  heading,
  description,
  isGenerating,
  onClick,
}: {
  iconSrc: string;
  heading: string;
  description: string;
  isGenerating: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex w-[210px] flex-col gap-4">
      <div
        className="border-grey-1 bg-grey-1 hover:border-default-blue relative flex w-full cursor-pointer flex-col items-center rounded border-[3px] p-4"
        onClick={onClick}>
        <img className="h-[172px]" src={iconSrc} />
        {isGenerating && (
          <div className="bg-grey-1 absolute z-10 flex h-[172px] w-full items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div className="border-default-grey-2 border-t-default-blue size-[28px] animate-spin rounded-full border-4" />
              <span className="text-default-text text-sm">Generating PDF...</span>
            </div>
          </div>
        )}
      </div>
      <div className="text-default-text flex flex-col items-center gap-2">
        <div className="text-base font-bold">{heading}</div>
        <div className="text-default-text text-center text-sm">{description}</div>
      </div>
    </div>
  );
}
